import { Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from "@mui/material";
import { makeStyles } from "@mui/styles";

import React, { useState } from "react";
import { CustomTable, SearchTableInput } from "../../../components";
import { useNavigate } from "react-router-dom";
import { routes } from "../../../routing/routes";
import { userTableHeading } from "../../../assets/lib/user";
import { useMediaQuery } from '@mui/material';
import { db } from "../../../config/firebase";
import { collection, getDocs } from "firebase/firestore";

const customStyle = makeStyles({
  root: {
    padding: "20px 40px",
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "30px",
    overflow: "hidden",
  },
  buttonContainer: {
    height: "50px",
    display: "flex",
    justifyContent: "space-between",
  },
  actionBtn: {
    display: "flex",
    gap: "15px",
  },
  '@media (max-width: 390px)': {
    root: {
      padding: "5px 5px",
      width: "80%",
      display: "flex",
      flexDirection: "column",
      gap: "40px",
      overflow: "hidden",
    },
    buttonContainer: {
      height: "50px",
      display: "flex",
      justifyContent: "start",
      flexWrap:"wrap",
      gap: "10px",
    },
    actionBtn: {
      display: "flex",
      gap: "10px",
    },
  },
});

const Layout = (props) => {
  const {
    // User table props
    userTableData,
    isTableLoading,
    currentPage,
    totalLength,
    handlePageChange,
    handlePageSize,
    handleSearchQuery,
    handleTableSorting,

    // Recharge offers props
    offers,
    offerModalOpen,
    setOfferModalOpen,
    offerForm,
    handleOfferInputChange,
    handleSaveOffer,
    handleEditOffer,
    handleDeleteOffer,
    isEditing,
  } = props;

  const classes = customStyle();
  const navigate = useNavigate();
  const isPhone = useMediaQuery('(max-width: 390px)');

  // Wallet history state
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [walletHistory, setWalletHistory] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);

  // 🔧 helper to handle both Firestore Timestamp & string date
  const getDate = (ts) => {
    if (!ts) return new Date();
    if (ts.toDate) return ts.toDate(); // Firestore Timestamp object
    return new Date(ts); // ISO string
  };

  // Fetch wallet history
  const fetchWalletHistory = async (phoneNumber) => {
    try {
      const phoneKey = String(phoneNumber); // 🔑 ensure string path
      const q = collection(db, "WalletHistory", phoneKey, "Entries");
      const snapshot = await getDocs(q);
      const list = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      // sort by timestamp desc
      list.sort((a, b) => getDate(b.timestamp) - getDate(a.timestamp));
      setWalletHistory(list);
    } catch (error) {
      console.error("Error fetching wallet history:", error);
    }
  };

  const actions = [
    {
      label: "View",
      onClick: (row) => {
        navigate(`${routes.USER_INFO}/${row.id}`, {
          state: { add: false, edit: false },
        });
      },
      bgColor: "#2196F3",
    },
    {
      label: "Edit",
      onClick: (row) => {
        navigate(`${routes.USER_INFO}/${row.id}`, {
          state: { add: false, edit: true },
        });
      },
      bgColor: "#4CAF50",
    },
    {
      label: "History",
      onClick: async (row) => {
        setSelectedUser(row);
        await fetchWalletHistory(String(row.phoneNumber)); // 🔑 force string here too
        setHistoryModalOpen(true);
      },
      bgColor: "#2196F3",
    },
  ];

  return (
    <div className={classes.root}>
      <div className={classes.buttonContainer}>
        <SearchTableInput
          tableHeading={userTableHeading}
          handleSearchQuery={handleSearchQuery}
        />
        <div className={classes.actionBtn}>
          <Button
            variant="contained"
            style={{
              color: "white",
              backgroundColor: "#FF5722",
              padding: isPhone ? "6px 12px" : "12px 24px",
              borderRadius: isPhone ? "5px" : "10px",
              fontSize: isPhone ? "10px" : "inherit",
            }}
            onClick={() =>
              navigate(routes.USER_INFO, {
                state: { add: true, edit: false },
              })
            }
          >
            Add User
          </Button>
          <Button
            variant="contained"
            style={{
              color: "white",
              backgroundColor: "#3f51b5",
              padding: isPhone ? "6px 12px" : "12px 24px",
              borderRadius: isPhone ? "5px" : "10px",
              fontSize: isPhone ? "10px" : "inherit",
            }}
            onClick={() => setOfferModalOpen(true)}
          >
            Recharge Offers
          </Button>
        </div>
      </div>

      <CustomTable
        columns={userTableHeading}
        data={userTableData}
        actions={actions}
        isLoading={isTableLoading}
        currentPage={currentPage}
        totalLength={totalLength}
        handlePageChange={handlePageChange}
        handlePageSize={handlePageSize}
        handleTableSorting={handleTableSorting}
      />

      {/* Recharge Offers Modal */}
      <Dialog open={offerModalOpen} onClose={() => setOfferModalOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Recharge Offers</DialogTitle>
        <DialogContent>
          {offers.length > 0 ? (
            offers.map((offer) => (
              <div key={offer.id} style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}>
                <span>
                  ₹{offer.rechargeAmount} → +₹{offer.walletAmount} (From {offer.startDate} to {offer.endDate})
                </span>
                <div>
                  <Button size="small" color="primary" onClick={() => handleEditOffer(offer)}>Edit</Button>
                  <Button size="small" color="secondary" onClick={() => handleDeleteOffer(offer.id)}>Delete</Button>
                </div>
              </div>
            ))
          ) : (
            <p>No offers found</p>
          )}
          <div style={{ marginTop: "20px" }}>
            <TextField
              label="Recharge Amount"
              name="rechargeAmount"
              value={offerForm.rechargeAmount}
              onChange={handleOfferInputChange}
              fullWidth
              margin="dense"
            />
            <TextField
              label="Wallet Amount"
              name="walletAmount"
              value={offerForm.walletAmount}
              onChange={handleOfferInputChange}
              fullWidth
              margin="dense"
            />
            <TextField
              label="Start Date"
              type="date"
              name="startDate"
              value={offerForm.startDate}
              onChange={handleOfferInputChange}
              fullWidth
              margin="dense"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="End Date"
              type="date"
              name="endDate"
              value={offerForm.endDate}
              onChange={handleOfferInputChange}
              fullWidth
              margin="dense"
              InputLabelProps={{ shrink: true }}
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOfferModalOpen(false)}>Close</Button>
          <Button color="primary" onClick={handleSaveOffer}>
            {isEditing ? "Update Offer" : "Add Offer"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Wallet History Modal */}
      <Dialog open={historyModalOpen} onClose={() => setHistoryModalOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Wallet History - {selectedUser?.userName}</DialogTitle>
        <DialogContent>
          {walletHistory.length > 0 ? (
            walletHistory.map((entry) => {
              const entryDate = getDate(entry.timestamp);
              return (
                <div
                  key={entry.id}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: "8px 0",
                    borderBottom: "1px solid #ddd",
                  }}
                >
                  <div>
                    <strong>{entryDate.toLocaleDateString()}</strong>
                    <div style={{ fontSize: "12px", color: "#666" }}>
                      {entryDate.toLocaleTimeString()}
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div>Prev: ₹{entry.previousAmount}</div>
                    <div>+₹{entry.addedAmount}</div>
                    <div><b>New: ₹{entry.newAmount}</b></div>
                  </div>
                </div>
              );
            })
          ) : (
            <p>No wallet history found.</p>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default Layout;
